import { scaleLinear } from "d3-scale"
import { line, curveLinear } from "d3-shape"
import { axisLeft, axisBottom } from "d3-axis"
import { get, orderBy, filter } from "lodash"
import { createSelector } from "reselect"

export const MARGINS = { top: 15, left: 60, right: 0, bottom: 40 }

export const getData = props => props.data
export const getDots = props => props.dots
export const getWidth = props => props.width || 150
export const getHeight = props => props.height
const getXAxisLabel = props => props.xAxisLabel
const getYAxisLabel = props => props.yAxisLabel
const getHighlighterLabel = props => props.highlighterLabel
const getTarget = props => props.target

export const getFullDataSet = createSelector(
  [getData, getDots],
  (data, dots) => orderBy([...data, ...dots], "x"),
)

export const getApproximateDataSet = createSelector(
  [getData, getDots],
  (data, dots) => {
    const beforeScale = filter(dots, dot => dot.x < data[0].x)
    const afterScale = filter(dots, dot => dot.x > data[data.length - 1].x)
    return orderBy([...beforeScale, ...data, ...afterScale], "x")
  },
)

export const getMinX = createSelector(
  [getFullDataSet],
  (data) => data.reduce((min, datum) => {
    return Math.min(min, datum.x)
  }, Infinity),
)

export const getMaxX = createSelector(
  [getFullDataSet],
  (data) => data.reduce((max, datum) => {
    return Math.max(max, datum.x)
  }, -Infinity),
)

export const getMinY = createSelector(
  [getFullDataSet],
  (data) => data.reduce((min, datum) => {
    return Math.min(min, datum.y)
  }, Infinity),
)

export const getMaxY = createSelector(
  [getFullDataSet],
  (data) => data.reduce((max, datum) => {
    return Math.max(max, datum.y)
  }, -Infinity),
)

export const getScaleX = createSelector(
  [getMinX, getMaxX, getWidth],
  (minX, maxX, width) => scaleLinear()
    .domain([minX, maxX])
    .rangeRound([MARGINS.left + 20, width - MARGINS.right - 20]),
)

export const getScaleY = createSelector(
  [getMinY, getMaxY, getHeight],
  (minY, maxY, height) => {
    return scaleLinear()
      .domain([minY, maxY])
      .rangeRound([height - MARGINS.bottom - 20, MARGINS.top + 20])
  },
)

export const getLine = createSelector(
  [getScaleX, getScaleY, getData],
  (scaleX, scaleY, data) => ({
    d: line()
      .x(d => scaleX(d.x))
      .y(d => scaleY(get(d, "y", 0)))
      .curve(curveLinear)(data),
  }),
)

export const getXAxis = createSelector(
  [getHeight, getWidth, getScaleX, getXAxisLabel],
  (height, width, scaleX, axisLabel) => {
    return ({
      fn: axisBottom()
        .scale(scaleX)
        .ticks(10)
        .tickSize(0),
      transform: `translate(0,${height - MARGINS.bottom + 5})`,
      label: {
        text: axisLabel,
        transform: `translate(${width / 2},${height})`,
      },
      baseline: {
        x1: MARGINS.left,
        x2: width - MARGINS.right,
        y1: height - MARGINS.bottom,
        y2: height - MARGINS.bottom,
      },
    })
  },
)

export const getYAxis = createSelector(
  [getHeight, getWidth, getScaleY, getYAxisLabel],
  (height, width, scaleY, axisLabel) => ({
    fn: axisLeft()
      .scale(scaleY)
      .ticks(5)
      .tickSize(0),
    transform: `translate(${MARGINS.left - 5},0)`,
    label: {
      text: axisLabel,
      transform: "rotate(-90)",
      y: 0,
      x: 0 - (height / 2),
      dy: 20,
    },
    baseline: {
      x1: MARGINS.left,
      x2: MARGINS.left,
      y1: MARGINS.top,
      y2: height - MARGINS.bottom,
    },
  }),
)

export const getHighlighter = createSelector(
  [getScaleX, getScaleY, getMinY, getMaxY, getHighlighterLabel, getTarget],
  (scaleX, scaleY, minY, maxY, highlighterLabel, target) => {
    if (target) {
      const x = scaleX(target.x)
      const highlighter = {
        x1: x,
        x2: x,
        y1: scaleY(minY) + 45, // Makes the highlighter overflow of 25px below the chart.
        y2: scaleY(maxY) - 20, // Makes the highlighter overflow of 5px above the chart.
      }

      return {
        label: {
          x: highlighter.x1,
          y: 10,
          text: highlighterLabel,
        },
        line: highlighter,
      }
    }

    return null
  },
)
